/**
 * @fileoverview Module for `src/components/MailingList.js`.
 */

import axios from 'axios';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import logger from '../lib/logger';
import Tracker from '../lib/Tracker';
import { AuthContext } from '../contexts/AuthContext';

import '../scss/lib/_forms.scss';

/**
 * Newsletter signup form with inline validation and tracking.
 */
class MailingList extends Component {
  static contextType = AuthContext;

  /**
   * Initializes email input, error, and submission state.
   *
   * @param {Object} props - Component props.
   * @param {Function} props.t - i18next translation function.
   */
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      emailError: '',
      formSubmitSuccess: false,
    };
    this.tracker = new Tracker({ auth: this.context });
  }

  /**
   * Submits an email signup request for the mailing list.
   *
   * @returns {Promise<void>} Resolves after request handling completes.
   */
  submitMailingList = async () => {
    const payload = {
      email: this.state.email,
      newsletter: true,
    };
    this.setState({ emailError: '' });
    if (this.state.email === '') {
      this.setState({ emailError: this.props.t('error.required') });
    } else if (!this.validateEmail(this.state.email)) {
      this.setState({ emailError: this.props.t('error.valid') });
    } else {
      try {
        await axios.post(`${ process.env.REACT_APP_API_URL || '' }/v1/emails/mailing-lists`, payload);
        this.setState({ formSubmitSuccess: true });
        this.tracker.logEvent('EmailPreferences:function:mailingList', { status: 200 });
      } catch (error) {
        logger.error(error);
        this.setState({ emailError: this.props.t('error.signUp') });
        this.tracker.logEvent('EmailPreferences:function:mailingList', { status: 500 });
      }
    }
  };

  /**
   * Validates email format for newsletter signup.
   *
   * @param {string} email
   * @returns {boolean} True when email appears valid.
   */
  validateEmail(email) {
    // eslint-disable-next-line regexp/no-unused-capturing-group
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  /**
   * Updates the email input value and clears any validation error.
   *
   * @param {Event} event - The input change event.
   */
  handleChange = (event) => {
    this.setState({ emailError: '' });
    this.setState({ email: event.target.value });
  };

  /**
   * Submits the mailing list form when Enter is pressed.
   *
   * @param {KeyboardEvent} e - The keydown event.
   */
  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      this.submitMailingList();
    }
  };

  /**
   * Renders the email signup form with validation, or a success
   * confirmation message after submission.
   *
   * @returns {JSX.Element} The mailing list signup form.
   */
  render() {
    return this.state.formSubmitSuccess
      ? (
        <p className='pt-3 text-center text-purple'>
          { this.props.t('success', { email: this.state.email }) }
        </p>
        )
      : (
        <div>
          <input
            className='form-control me-2 mb-2 mb-md-0 pt-2 pb-2'
            name='email'
            onChange={ this.handleChange }
            onKeyDown={ this.handleKeyDown }
            placeholder={ this.props.t('placeholder') }
            type='email'
            value={ this.state.email }
          />
          {
            this.state.emailError === ''
              ? null
              : (
                <p className='pt-2 text-center text-danger'>
                  { this.state.emailError }
                </p>
                )
          }
          <button
            className={ classNames('btn btn-primary col-12 col-sm-4 d-block mt-3 mx-auto', { disabled: this.state.disableButton }) }
            disabled={ this.state.disableButton }
            onClick={ this.submitMailingList }
            style={{ color: 'white' }}
          >
            { this.props.t('button') }
          </button>
        </div>
        );
  }
}

MailingList.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('mailingList')(MailingList);
